import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import "./styles/AdminMessages.css";

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState([]);
  const [csrfToken, setCsrfToken] = useState("");

  useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrfToken="))
      ?.split("=")[1];
    if (token) setCsrfToken(token);
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get("/api/admin/messages");
      setMessages(response.data.messages);
    } catch (error) {
      console.error("🔥 Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  // Expand/collapse row
  const toggleRow = (msgId) => {
    setExpandedRows((prev) =>
      prev.includes(msgId)
        ? prev.filter((id) => id !== msgId)
        : [...prev, msgId]
    );
  };

  // Archive/Unarchive
  const handleArchiveMessage = async (id, archived) => {
    if (!csrfToken) return console.error("🚨 CSRF token is missing.");
    try {
      await axios.put(
        `/api/admin/messages/${id}/archive`,
        { archived: !archived },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === id ? { ...msg, archived: !archived } : msg
        )
      );
    } catch (error) {
      console.error("🔥 Error archiving message:", error);
    }
  };

  // Delete
  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message permanently?")) return;
    if (!csrfToken) return console.error("🚨 CSRF token is missing.");

    try {
      await axios.delete(`/api/admin/messages/${id}`, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (error) {
      console.error("🔥 Error deleting message:", error);
    }
  };

  // Utility for rendering a table
  const renderTable = (messagesSubset, isArchived = false) => (
    <div className="table-wrapper">
      <table className={`messages-table${isArchived ? " archived" : ""}`}>
        <thead>
          <tr>
            <th className="expand-btn-cell header"><i className="fa-solid fa-chevron-down"></i></th>
            <th>Name</th>
            <th>Address</th>
            <th>Email / Phone</th>
            <th>Project Needs</th>
            <th>Submitted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {messagesSubset.map((msg) => (
            <React.Fragment key={msg.id}>
              <tr>
                <td className="expand-btn-cell" onClick={() => toggleRow(msg.id)} aria-label={expandedRows.includes(msg.id) ? "Collapse details" : "Expand details"}>
                  <button
                    className={`expand-btn ${expandedRows.includes(msg.id) ? "expanded" : ""}`}
                  >
                    <p><i className="fa-solid fa-chevron-down"></i></p>
                  </button>
                </td>
                <td>
                  {msg.first_name} {msg.last_name}
                </td>
                <td>
                  {msg.address || <span style={{ color: "#bbb" }}>[None provided]</span>}
                </td>
                <td>
                  <div><b>Email: </b>{msg.email}</div>
                  <div><b>Phone: </b>{msg.phone}</div>
                </td>
                <td style={{ maxWidth: 300, wordBreak: "break-word" }}>{msg.project_needs}</td>
                <td>
                  {msg.submitted_at ? (
                    <span className="submitted-datetime">
                      <span className="date">
                        {new Date(msg.submitted_at).toLocaleDateString()}
                      </span>
                      <br />
                      <span className="time">
                        {new Date(msg.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </span>
                  ) : ""}
                </td>
                <td className="table-actions">
                  <button
                    className="btn archive-btn"
                    onClick={() => handleArchiveMessage(msg.id, !!msg.archived)}
                    title={isArchived ? "Unarchive" : "Archive"}
                  >
                    <i className="fa-solid fa-box-archive"></i>
                    {isArchived ? " Unarchive" : " Archive"}
                  </button>
                  <button
                    className="btn delete-btn"
                    onClick={() => handleDeleteMessage(msg.id)}
                    title="Delete"
                  >
                    <i className="fa-solid fa-xmark"></i> Delete
                  </button>
                </td>
              </tr>
              <tr className="order-details-row">
                <td colSpan={7}>
                  <div className={`order-details-wrapper${expandedRows.includes(msg.id) ? " expanded" : ""}`}>
                    <div className="order-details">
                      <div className="order-shipping-info">
                        <h3>Additional Information</h3>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ marginTop: 4, whiteSpace: "pre-line" }}>
                            {msg.additional_info || <span style={{ color: "#bbb" }}>[None provided]</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Filter
  const activeMessages = messages.filter(msg => !msg.archived);
  const archivedMessages = messages.filter(msg => !!msg.archived);

  return (
    <div className="admin-messages-container">
      <h2 className="messages-title">Contact Messages</h2>
      {loading ? (
        <p className="messages-loading">Loading messages...</p>
      ) : messages.length === 0 ? (
        <p className="messages-empty">No messages found.</p>
      ) : (
        <>
          <div className="table-section">
            <h3 className="table-section-title">Active Messages</h3>
            {renderTable(activeMessages, false)}
          </div>
          <div className="table-section">
            <h3 className="table-section-title">Archived Messages</h3>
            {archivedMessages.length === 0 ? (
              <div className="messages-empty">No archived messages.</div>
            ) : (
              renderTable(archivedMessages, true)
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminMessages;
